import type { CourseDto, ContentLanguageDto } from '../dto/course.dto'
import type { Course } from '@/domain/entities/course.entity'
import type { CurriculumOverviewParts } from '@/domain/entities/curriculum.entity'
import type { ContentLanguage } from '@/domain/entities/content-language.entity'
import { mapDate, mapDateToString, mapNumber, mapString, mapBoolean } from './base.mapper'
import { getLocaleEndonym } from '@/utils/locale'

/**
 * Маппер для преобразования CourseDto (бэкенд) -> Course (Domain Entity)
 * Преобразует поля бэкенда в удобные названия для фронтенда
 */
export function mapCourseDtoToEntity(dto: CourseDto | Record<string, unknown>): Course {
  const d = dto as any
  return {
    id: d._id, // _id из бэкенда -> id в domain
    title: d.course_title || '', // course_title -> title
    slug: d.course_url_token || '', // course_url_token -> slug
    description: d.course_description, // course_description -> description
    courseObjectives: d.course_objectives, // course_objectives -> courseObjectives
    imageUrl: d.course_image, // course_image -> imageUrl
    logoUrl: d.course_logo, // course_logo -> logoUrl
    duration: mapNumber(d.course_minutes), // course_minutes -> duration
    level: mapNumber(d.course_level), // course_level -> level (number)
    lessonsAmount: mapNumber(d.course_lessons_amount), // course_lessons_amount -> lessonsAmount
    lessonsStudied: mapNumber(d.course_lessons_studied), // course_lessons_studied -> lessonsStudied
    locale: d.course_locale, // course_locale -> locale
    category: d.course_category, // course_category -> category
    version: d.course_version, // course_version -> version
    optimized: mapBoolean(d.course_optimized), // course_optimized -> optimized
    isPublished: mapBoolean(d.course_is_published), // course_is_published -> isPublished
    isPublic: mapBoolean(d.course_is_public), // course_is_public -> isPublic
    top: mapBoolean(d.course_top), // course_top -> top
    ownerAccountId: d.course_owner_account_id, // course_owner_account_id -> ownerAccountId
    // author — присутній тільки в CourseDetailsResponseDto
    author: d.author ?? undefined,
    // createdAt и updatedAt не приходят из бэкенда в CourseResponse
  }
}

/**
 * Маппер для массива CourseDto -> Course[]
 */
export function mapCourseDtoArrayToEntities(dtos: CourseDto[]): Course[] {
  return dtos.map(mapCourseDtoToEntity)
}

/**
 * Маппинг Course в части для overview программы (lessonsAmount, duration, hours, minutes).
 * Дальше форматирование — через formatCurriculumOverview(parts, t) в utils.
 */
export function mapCourseToCurriculumOverviewParts(course: Course | null): CurriculumOverviewParts | null {
  if (course?.lessonsAmount == null) return null
  const duration: number = course.duration ?? 0
  const result: CurriculumOverviewParts = {
    lessonsAmount: course.lessonsAmount,
    duration,
    hours: Math.floor(duration / 60),
    minutes: duration % 60,
  }
  return result
}

/**
 * Маппер для преобразования ContentLanguageDto (бэкенд) -> ContentLanguage (Domain Entity)
 */
export function mapContentLanguageDtoToEntity(dto: ContentLanguageDto): ContentLanguage {
  return {
    id: dto.id,
    label: dto.label,
    endonym: getLocaleEndonym(dto.id),
    courseCount: dto.course_count,
    isDefault: dto.is_default,
  }
}

/**
 * Маппер для массива ContentLanguageDto -> ContentLanguage[]
 */
export function mapContentLanguageDtoArrayToEntities(dtos: ContentLanguageDto[]): ContentLanguage[] {
  return dtos.map(mapContentLanguageDtoToEntity)
}
